import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, X } from "@digital-twins/design-system/icons";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/Text";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChatThread } from "@/pages/ChatPage/ChatThread/ChatThread";
import { ParticipantAvatarStack } from "@/pages/ChatPage/FocusGroupSession/FocusGroupSession";
import { authenticatedChatRoomTransport } from "@/pages/ChatPage/chatRoomTransport";
import { useChatRoom } from "@/pages/ChatPage/useChatRoom";
import { getChatPlaceholders } from "@/lib/constants/languages";
import { ROUTES } from "@/routes/routes";
import Link from "@/routing/Link";

interface RoomSessionProps {
  roomToken: string;
  initialMessage?: string;
  onInitialMessageQueued?: () => void;
  onClose: () => void;
}

/** The existing room transport, queue and composer hosted inside the thread pane. */
export function RoomSession(props: RoomSessionProps) {
  const { roomToken, initialMessage, onInitialMessageQueued, onClose } = props;
  const { t } = useTranslation("app");
  const room = useChatRoom(roomToken, { enableRouteFallback: false });
  const hasUserMessage = room.messages.some(
    (message) => message.senderType === "user",
  );
  const openingQueuedFor = useRef<string | null>(null);
  useEffect(() => {
    if (
      openingQueuedFor.current === roomToken ||
      !initialMessage ||
      !room.roomData ||
      room.roomData.token !== roomToken ||
      room.roomLoading ||
      room.messagesLoading ||
      room.roomError ||
      room.messagesError ||
      room.isPreparing ||
      room.isError ||
      room.isClosed ||
      room.atMessageLimit ||
      room.participants.length === 0
    ) {
      return;
    }
    if (hasUserMessage) {
      openingQueuedFor.current = roomToken;
      onInitialMessageQueued?.();
      return;
    }
    // handleSend owns busy-room queuing and durable delivery identity. Only
    // consume the handoff after that queue accepts it, including while busy.
    const requestId = room.handleSend(initialMessage);
    if (requestId) {
      openingQueuedFor.current = roomToken;
      onInitialMessageQueued?.();
    }
  }, [initialMessage, onInitialMessageQueued, room, roomToken, hasUserMessage]);

  if (room.roomError) {
    return (
      <div role="alert" className="p-4">
        <Text>{t("threads.sessions.roomLoadFailed")}</Text>
      </div>
    );
  }
  if (room.roomLoading || !room.roomData) {
    return <Skeleton className="m-4 h-32" data-testid="room-session-loading" />;
  }
  const placeholders = getChatPlaceholders(
    room.roomData.settings.language ?? "en",
  );
  return (
    <div className="@container/room flex min-h-0 flex-1 flex-col" data-testid="room-session">
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
        <ParticipantAvatarStack
          participants={room.participants}
          populationId={room.roomData.populationId}
        />
        <Text size="sm" className="min-w-0 flex-1 truncate" title={room.roomTitle || t("threads.sessions.rooms")}>
          {room.roomTitle || t("threads.sessions.rooms")}
        </Text>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 shrink-0 gap-2 px-2" asChild>
                <Link
                  href={ROUTES.chatRoom(roomToken)}
                  data-testid="room-session-expand"
                  aria-label={t("quickStudyChrome.openFullConversation")}
                >
                  <Maximize2 className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden @[36rem]/room:inline">{t("quickStudyChrome.openFullConversation")}</span>
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("quickStudyChrome.openFullConversation")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          data-testid="room-session-close"
          aria-label={t("threads.sessions.hide")}
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      {room.isError ? (
        <div role="alert" className="px-3 py-2">
          <Text size="sm">{t("threads.sessions.failed")}</Text>
        </div>
      ) : null}
      <ChatThread
        messages={room.messages}
        participants={room.participants}
        roomStatus={room.roomStatus}
        onSend={room.handleSend}
        composerOverride={room.isError ? <></> : undefined}
        thinkingParticipantIds={room.thinkingParticipantIds}
        isSending={room.isSending}
        isWaitingForTurn={room.isWaitingForTurn}
        queueBlockedReason={room.queueBlockedReason}
        messageQueue={room.messageQueue}
        onQueueEdit={room.handleQueueEdit}
        onQueueRemove={room.handleQueueRemove}
        onQueueRetry={room.handleQueueRetry}
        blockedQueuedId={room.blockedQueuedId}
        sendError={room.sendError}
        messagesError={room.messagesError}
        onRetryMessages={room.retryMessages}
        atMessageLimit={room.atMessageLimit}
        isRoomActive={room.roomStatus === "active" && hasUserMessage}
        roomToken={roomToken}
        getImageUrl={authenticatedChatRoomTransport.getImageUrl}
        reactions={room.reactions}
        liveReactionKeys={room.liveReactionKeys}
        populationId={room.roomData.populationId}
        placeholder={
          room.participants.length === 1
            ? placeholders.interview(room.participants[0].displayName)
            : placeholders.groupFallback
        }
        emptyHeadline={
          room.participants.length === 1
            ? placeholders.emptyHeadlineInterview(
                room.participants[0].displayName,
              )
            : placeholders.emptyHeadlineGroup
        }
      />
    </div>
  );
}
